import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | timeout | error
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setInfo(data); setState("paid"); return; }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div data-testid="payment-status-card" className="bg-card border border-border rounded-2xl p-10 max-w-md w-full text-center shadow-xl">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-accent mx-auto mb-4" />
            <h1 className="font-heading text-xl font-medium tracking-tight mb-2">Verifying payment…</h1>
            <p className="text-sm text-muted-foreground">Hang tight, confirming with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 data-testid="payment-success-icon" className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-medium tracking-tight mb-2">Upgrade unlocked</h1>
            <p className="text-sm text-muted-foreground mb-2">
              {info?.tier_name ? `${info.tier_name} for ${info.app_name}` : "Your purchase"} is now active.
            </p>
            <p className="font-mono text-xs text-muted-foreground mb-6">
              Only your payment status was stored. Nothing else. Ever.
            </p>
          </>
        )}
        {(state === "expired" || state === "timeout" || state === "error") && (
          <>
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="font-heading text-xl font-medium tracking-tight mb-2">
              {state === "expired" ? "Session expired" : state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {state === "timeout"
                ? "We couldn't confirm in time. If you were charged, your unlock will activate automatically."
                : "No charge was completed. Feel free to try again."}
            </p>
          </>
        )}
        {state !== "checking" && (
          <Button asChild variant="outline" className="rounded-full" data-testid="back-home-btn">
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" />Back to showcase</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
